import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { seoAPI } from '../../services/api';
import { ScoreBadge, shortUrl, SeoResult } from './seoTypes';
import SeoKeywordGate from './SeoKeywordGate';

// ─── Keyword Manager (compact) ────────────────────────────────────────────────

const KeywordBar: React.FC<{ assetId: number }> = ({ assetId }) => {
  const [keywords, setKeywords] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    seoAPI.getKeywords(assetId).then(res => {
      setKeywords(res.data.data.keywords.map((k: any) => k.keyword));
    }).catch(() => {});
  }, [assetId]);

  const add = () => {
    const kw = input.trim();
    if (!kw || keywords.includes(kw)) return;
    setKeywords(prev => [...prev, kw]);
    setInput('');
  };

  const save = async () => {
    setSaving(true);
    try {
      await seoAPI.saveKeywords(assetId, keywords);
      toast.success('Keywords saved');
      setOpen(false);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const autoExtract = async () => {
    setExtracting(true);
    try {
      const res = await seoAPI.autoExtractKeywords(assetId);
      const s: string[] = res.data.data.suggestions || [];
      setKeywords(prev => [...prev, ...s].filter((v, i, a) => a.indexOf(v) === i).slice(0, 20));
      toast.success(`${s.length} keywords extracted from your site`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Extraction failed');
    } finally { setExtracting(false); }
  };

  return (
    <div className="card border-0 shadow-sm mb-4">
      <div
        className="card-header bg-white border-0 d-flex align-items-center justify-content-between py-2 px-3"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <span className="fw-semibold small">
          <i className="bi bi-key text-primary me-2"></i>
          Target Keywords
          {keywords.length > 0 && (
            <span className="badge bg-primary ms-2" style={{ fontSize: '0.65rem' }}>{keywords.length}</span>
          )}
        </span>
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`}></i>
      </div>
      {open && (
        <div className="card-body pt-0">
          <p className="text-muted small mb-2">
            Keywords drive AI suggestions — e.g. <em>"Best IT company in Bahrain"</em>
          </p>
          <div className="d-flex flex-wrap gap-1 mb-2">
            {keywords.map(kw => (
              <span key={kw} style={{
                background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe',
                borderRadius: 20, padding: '3px 10px', fontSize: '0.78rem',
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>
                {kw}
                <button
                  onClick={() => setKeywords(p => p.filter(k => k !== kw))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, lineHeight: 1 }}
                >
                  <i className="bi bi-x"></i>
                </button>
              </span>
            ))}
            {keywords.length === 0 && <span className="text-muted small">No keywords yet</span>}
          </div>
          <div className="input-group input-group-sm mb-2">
            <input
              type="text"
              className="form-control"
              placeholder='e.g. "Best IT company in Bahrain"'
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && add()}
            />
            <button className="btn btn-outline-primary" onClick={add} disabled={!input.trim()}>Add</button>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm"></span> : 'Save'}
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={autoExtract} disabled={extracting}>
              {extracting ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-magic me-1"></i>}
              Auto-Extract from Site
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── AI Card ──────────────────────────────────────────────────────────────────

const AiCard: React.FC<{ result: SeoResult; onUpdate: (r: SeoResult) => void }> = ({ result, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const ai = result.ai_suggestions;

  const generate = async () => {
    setLoading(true);
    try {
      const res = await seoAPI.generateAI(result.id);
      onUpdate({ ...result, ai_suggestions: res.data.data.suggestions });
      toast.success('AI suggestions ready!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'AI generation failed');
    } finally { setLoading(false); }
  };

  const crit = (result.issues || []).filter(i => i.severity === 'critical').length;
  const warn = (result.issues || []).filter(i => i.severity === 'warning').length;

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body">
        {/* Header row */}
        <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold small text-truncate" style={{ maxWidth: 420 }}>
              <a href={result.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-dark">
                <i className="bi bi-link-45deg text-muted me-1"></i>{shortUrl(result.url, 60)}
              </a>
            </div>
            {result.page_title && (
              <div className="text-muted" style={{ fontSize: '0.72rem' }}>{result.page_title.slice(0, 60)}</div>
            )}
            <div className="d-flex gap-1 mt-1 flex-wrap">
              {crit > 0 && (
                <span style={{ background: '#fee2e2', color: '#dc2626', fontSize: '0.68rem', padding: '1px 6px', borderRadius: 5 }}>
                  {crit} critical
                </span>
              )}
              {warn > 0 && (
                <span style={{ background: '#fef3c7', color: '#d97706', fontSize: '0.68rem', padding: '1px 6px', borderRadius: 5 }}>
                  {warn} warnings
                </span>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <ScoreBadge score={result.seo_score} />
            {!ai ? (
              <button className="btn btn-primary btn-sm" onClick={generate} disabled={loading} style={{ whiteSpace: 'nowrap' }}>
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Generating…</>
                  : <><i className="bi bi-stars me-1"></i>Generate AI Suggestions</>}
              </button>
            ) : (
              <button className="btn btn-outline-secondary btn-sm" onClick={generate} disabled={loading} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                {loading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-arrow-clockwise me-1"></i>Regenerate</>}
              </button>
            )}
          </div>
        </div>

        {/* Top issues if no AI yet */}
        {!ai && (result.issues || []).filter(i => i.severity !== 'info').slice(0, 3).map((issue, i) => (
          <div key={i} className="d-flex gap-2 mb-1 small">
            <i className={`bi ${issue.severity === 'critical' ? 'bi-x-circle-fill text-danger' : 'bi-exclamation-triangle-fill text-warning'} flex-shrink-0 mt-1`} style={{ fontSize: '0.7rem' }}></i>
            <span className="text-muted">{issue.message}</span>
          </div>
        ))}

        {/* AI output */}
        {ai && (
          <>
            <div className="row g-2 mb-3">
              {[
                { label: 'Title', old: result.page_title, newVal: ai.improved_title },
                { label: 'H1', old: result.page_data?.h1, newVal: ai.improved_h1 },
              ].map(f => (
                <div key={f.label} className="col-12 col-md-6">
                  <div className="rounded p-2" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div className="small fw-semibold text-muted mb-1">{f.label}</div>
                    {f.old && <div className="small text-danger text-decoration-line-through mb-1 text-truncate">{f.old}</div>}
                    <div className="small fw-semibold text-success">{f.newVal}</div>
                  </div>
                </div>
              ))}
              <div className="col-12">
                <div className="rounded p-2" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <div className="small fw-semibold text-muted mb-1">Meta Description</div>
                  {result.meta_description && (
                    <div className="small text-danger text-decoration-line-through mb-1">{result.meta_description}</div>
                  )}
                  <div className="small fw-semibold text-success">{ai.improved_meta}</div>
                </div>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <div className="small fw-semibold text-muted mb-1">
                  <i className="bi bi-lightbulb text-warning me-1"></i>Content Tips
                </div>
                {ai.content_tips.map((tip, i) => (
                  <div key={i} className="d-flex gap-2 small mb-1">
                    <span className="text-muted flex-shrink-0">•</span>{tip}
                  </div>
                ))}
              </div>
              <div className="col-12 col-md-6">
                <div className="small fw-semibold text-muted mb-1">
                  <i className="bi bi-robot text-primary me-1"></i>AI Search Tips (SGE / Perplexity)
                </div>
                {ai.ai_search_tips.map((tip, i) => (
                  <div key={i} className="d-flex gap-2 small mb-1">
                    <span className="text-muted flex-shrink-0">•</span>{tip}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const SeoOptimizerPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;

  const [results, setResults] = useState<SeoResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    seoAPI.getResults(assetId)
      .then(res => {
        const r: SeoResult[] = res.data.data.results || [];
        setResults(r.filter(x => x.status === 'completed').sort((a, b) => (a.seo_score || 0) - (b.seo_score || 0)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [assetId]);

  const handleUpdate = (updated: SeoResult) =>
    setResults(prev => prev.map(r => r.id === updated.id ? updated : r));

  if (!assetId) return <DashboardLayout><div className="p-4 text-muted">Select an asset first.</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <SeoKeywordGate assetId={assetId}>
      <div className="container-fluid py-3" style={{ maxWidth: 1000 }}>
        {/* Header */}
        <div className="mb-4">
          <h4 className="fw-bold mb-0">
            <i className="bi bi-stars text-primary me-2"></i>AI Optimizer
          </h4>
          <p className="text-muted small mb-0">
            AI-powered SEO rewrites tailored to your target keywords — pages ordered by improvement opportunity
          </p>
        </div>

        {/* Keyword bar */}
        {assetId && <KeywordBar assetId={assetId} />}

        {loading ? (
          <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
        ) : results.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="bi bi-stars" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
              <h5 className="mt-3 fw-bold">No scan results yet</h5>
              <p className="text-muted mb-4" style={{ maxWidth: 380, margin: '0 auto' }}>
                Run an SEO scan first, then come back here to get AI-powered title, meta description, H1, and content recommendations.
              </p>
              <Link to="/seo/scanner" className="btn btn-primary">
                <i className="bi bi-search me-2"></i>Go to Scanner
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 small text-muted">
              {results.length} pages — lowest scoring first (most opportunity).
              Click <strong>Generate AI Suggestions</strong> on any page to get keyword-aware rewrites.
            </div>
            {results.map(r => (
              <AiCard key={r.id} result={r} onUpdate={handleUpdate} />
            ))}
          </>
        )}
      </div>
      </SeoKeywordGate>
    </DashboardLayout>
  );
};

export default SeoOptimizerPage;
